import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PortalResponse, UserRoleResponse } from '@core/models/common.model';
import { ClientResponse } from '@features/client/client.model';
import { SearchableLookupOption, SearchableLookupSelectComponent } from '@shared/components/searchable-lookup-select/searchable-lookup-select.component';
import { CreateUserRequest, UserResponse } from '../../user.model';
import { UserInfoComponent } from '../user-info/user-info.component';
import { nullIfBlank } from '@shared/utils/payload-utils';

@Component({
  selector: 'register-user',
  imports: [ReactiveFormsModule, UserInfoComponent, SearchableLookupSelectComponent, ...SharedMaterialUIModule],
  templateUrl: './register-user.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``,
})
export class RegisterUserComponent {
  private readonly formBuilder = inject(FormBuilder);

  readonly busy = input(false);
  readonly createdUser = input<UserResponse | null>(null);
  readonly clients = input<ClientResponse[]>([]);
  readonly portals = input<PortalResponse[]>([]);
  readonly userRoles = input<UserRoleResponse[]>([]);
  readonly affiliateOptions = input<SearchableLookupOption[]>([]);
  readonly affiliateOptionsLoading = input(false);
  readonly submitted = output<CreateUserRequest>();
  readonly affiliateSearchChanged = output<string>();

  readonly registerForm = this.formBuilder.nonNullable.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    portalId: ['', Validators.required],
    userRoleId: ['', Validators.required],
    clientId: [''],
    affiliateId: [''],
  });

  private readonly selectedPortalId = toSignal(this.registerForm.controls.portalId.valueChanges, {
    initialValue: this.registerForm.controls.portalId.value,
  });

  constructor() {
    effect(() => {
      const portalName = this.portalName(this.selectedPortalId());

      if (portalName !== 'client') {
        this.registerForm.controls.clientId.setValue('', { emitEvent: false });
      }

      if (portalName !== 'affiliate') {
        this.registerForm.controls.affiliateId.setValue('', { emitEvent: false });
      }
    });
  }

  protected isClientPortal(): boolean {
    return this.portalName(this.selectedPortalId()) === 'client';
  }

  protected isAffiliatePortal(): boolean {
    return this.portalName(this.selectedPortalId()) === 'affiliate';
  }

  private portalName(portalId: string): string {
    return this.portals().find((item) => item.id === portalId)?.name?.toLowerCase() ?? '';
  }

  protected submit(): void {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    const value = this.registerForm.getRawValue();
    this.submitted.emit({
      firstName: value.firstName,
      lastName: value.lastName,
      email: value.email,
      password: value.password,
      portalId: value.portalId,
      userRoleId: value.userRoleId,
      clientId: nullIfBlank(value.clientId),
      affiliateId: nullIfBlank(value.affiliateId),
    });
  }
}
